import React, { useEffect, useState, useCallback } from "react";
import { Col, Row, Spinner, Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FiSettings, FiAlertTriangle, FiRefreshCw, FiSave,
  FiCheck, FiX, FiZap, FiMapPin, FiBarChart2,
  FiSliders, FiDollarSign, FiWifi, FiWifiOff, FiClock,
  FiChevronRight, FiShield, FiActivity,
} from "react-icons/fi";
import {
  apiGetDeliveryFeeConfig,
  apiGetDeliveryFeeConfigAudit,
  apiUpdateDeliveryFeeConfig,
  apiSetVendorComplexity,
} from "../api/index.js";
import { socket } from "../utilities/socket.js";
import AppSelect from "../components/ui/AppSelect";
import SettingHelp from "./Settings/settingsHelp";
import RoadDistanceHealth from "./Settings/RoadDistanceHealth";

// Who may edit comes from the Settings page (`canEdit`, from
// GET /api/platform-settings/access at the "manage" level: a super
// administrator or settings:manage). The server's canEditDeliveryConfig accepts
// the same people, so the page never offers a save the server refuses. It used
// to read a role list here (SUPERUSER, ADMIN) that disagreed with the server
// both ways: an Admin saw controls and got a 403, a SUPERADMIN saw read only.
const ACTION_NAVY = "#083860";

// ── Design tokens (Power BI–inspired) ─────────────────────────────────────────
const C = {
  bg:          "#f4f6fb",
  panel:       "#ffffff",
  header:      "#1a2b4a",
  accent:      "#e63757",
  accentSoft:  "#fff0f3",
  accentBorder:"#f9c8d0",
  blue:        "#2c7be5",
  blueSoft:    "#eaf2ff",
  teal:        "#00b69b",
  tealSoft:    "#e6faf7",
  orange:      "#f6961e",
  orangeSoft:  "#fff4e6",
  purple:      "#7048e8",
  purpleSoft:  "#f1edff",
  text:        "#1a2b4a",
  textMid:     "#4a5568",
  textLight:   "#8898aa",
  border:      "#e2e8f0",
  inputBg:     "#f8faff",
  shadow:      "0 2px 12px rgba(26,43,74,0.08)",
  shadowHover: "0 4px 20px rgba(26,43,74,0.14)",
  radius:      "10px",
  radiusSm:    "6px",
};

const S = {
  page: {
    background: C.bg,
    minHeight: "100vh",
    padding: "0 0 40px 0",
  },
  heroBand: {
    background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
    padding: "28px 32px 24px",
    marginBottom: 28,
    borderRadius: C.radius,
    color: "#fff",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
    boxShadow: "0 4px 24px rgba(26,43,74,0.18)",
  },
  heroTitle: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: "-0.3px",
    marginBottom: 4,
    color: "#fff",
  },
  heroSub: {
    fontSize: 13,
    color: "rgba(255,255,255,0.70)",
    marginBottom: 0,
  },
  heroSaved: {
    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11,
    color: "#4dffc3", background: "rgba(0,182,155,0.15)",
    border: "1px solid rgba(0,182,155,0.3)",
    borderRadius: 6, padding: "6px 12px",
  },
  heroWarning: {
    background: "rgba(230,55,87,0.18)",
    border: "1px solid rgba(230,55,87,0.4)",
    borderRadius: C.radiusSm,
    padding: "6px 14px",
    fontSize: 12,
    color: "#ffb3be",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginTop: 8,
  },
  kpiCard: (color, softBg) => ({
    background: C.panel,
    borderRadius: C.radius,
    padding: "18px 20px",
    boxShadow: C.shadow,
    borderLeft: `4px solid ${color}`,
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 20,
  }),
  kpiIcon: (color, softBg) => ({
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: softBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: 18,
    color,
  }),
  kpiVal: {
    fontSize: 20,
    fontWeight: 700,
    color: C.text,
    lineHeight: 1.1,
  },
  kpiLabel: {
    fontSize: 11,
    color: C.textLight,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginTop: 2,
  },
  panel: (accentColor) => ({
    background: C.panel,
    borderRadius: C.radius,
    boxShadow: C.shadow,
    marginBottom: 24,
    overflow: "hidden",
    border: `1px solid ${C.border}`,
  }),
  panelHead: (accentColor) => ({
    background: `linear-gradient(90deg, ${accentColor}12 0%, transparent 100%)`,
    borderBottom: `1px solid ${C.border}`,
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    gap: 10,
  }),
  panelHeadDot: (color) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: color,
    flexShrink: 0,
  }),
  panelTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: C.text,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 0,
  },
  panelSub: {
    fontSize: 11,
    color: C.textLight,
    marginLeft: "auto",
  },
  panelBody: {
    padding: "20px",
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: C.textMid,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 4,
    display: "block",
  },
  fieldHint: {
    fontSize: 11,
    color: C.textLight,
    marginTop: 3,
  },
  input: {
    background: C.inputBg,
    border: `1px solid ${C.border}`,
    borderRadius: C.radiusSm,
    fontSize: 13,
    color: C.text,
    padding: "7px 11px",
    width: "100%",
    outline: "none",
    transition: "border-color 0.15s",
  },
  tierBar: (pct, color) => ({
    height: 6,
    borderRadius: 3,
    background: `linear-gradient(90deg, ${color} ${pct}%, ${C.border} ${pct}%)`,
    marginTop: 6,
  }),
  splitBar: {
    height: 10,
    borderRadius: 5,
    overflow: "hidden",
    display: "flex",
    marginTop: 6,
    marginBottom: 2,
  },
  btnPrimary: (disabled) => ({
    background: disabled ? "#b0bec5" : ACTION_NAVY,
    border: "none",
    borderRadius: C.radiusSm,
    color: "#fff",
    fontWeight: 600,
    fontSize: 13,
    padding: "9px 22px",
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: "none",
    transition: "all 0.15s",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  }),
  btnSecondary: {
    background: ACTION_NAVY,
    border: "none",
    borderRadius: C.radiusSm,
    color: "#fff",
    fontWeight: 600,
    fontSize: 13,
    padding: "9px 18px",
    cursor: "pointer",
    transition: "all 0.15s",
  },
  btnBlue: {
    background: ACTION_NAVY,
    border: "none",
    borderRadius: C.radiusSm,
    color: "#fff",
    fontWeight: 600,
    fontSize: 12,
    padding: "7px 16px",
    cursor: "pointer",
    boxShadow: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  btnTeal: {
    background: ACTION_NAVY,
    border: "none",
    borderRadius: C.radiusSm,
    color: "#fff",
    fontWeight: 600,
    fontSize: 12,
    padding: "7px 16px",
    cursor: "pointer",
    boxShadow: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  simRow: (highlight) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: C.radiusSm,
    background: highlight ? C.tealSoft : "transparent",
    marginBottom: 3,
    fontSize: 13,
  }),
  simLabel: (highlight) => ({
    color: highlight ? C.teal : C.textMid,
    fontWeight: highlight ? 700 : 400,
  }),
  simValue: (highlight) => ({
    color: highlight ? C.teal : C.text,
    fontWeight: highlight ? 800 : 600,
    fontSize: highlight ? 16 : 13,
  }),
  complexityBadge: (color, soft) => ({
    background: soft,
    color: color,
    border: `1px solid ${color}30`,
    borderRadius: 20,
    padding: "2px 10px",
    fontSize: 11,
    fontWeight: 700,
    display: "inline-block",
  }),
  successBanner: {
    background: C.tealSoft,
    border: `1px solid ${C.teal}40`,
    borderRadius: C.radiusSm,
    padding: "10px 14px",
    fontSize: 12,
    color: C.teal,
    marginTop: 12,
  },
  divider: {
    border: "none",
    borderTop: `1px solid ${C.border}`,
    margin: "14px 0",
  },
  sectionBreak: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: C.textLight,
    margin: "18px 0 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
};

const COMPLEXITY_META = {
  standard:  { label: "Standard",  color: C.textMid,  soft: "#f0f2f5"      },
  moderate:  { label: "Moderate",  color: C.blue,     soft: C.blueSoft     },
  difficult: { label: "Difficult", color: C.orange,   soft: C.orangeSoft   },
  premium:   { label: "Premium",   color: C.accent,   soft: C.accentSoft   },
};

const COMPLEXITY_ICONS = {
  standard:  <FiShield  size={12} />,
  moderate:  <FiBarChart2 size={12} />,
  difficult: <FiAlertTriangle size={12} />,
  premium:   <FiZap size={12} />,
};

// Compact number input using raw HTML
function NumInput({ value, onChange, min = 0, max, step = 1, style = {} }) {
  return (
    <input
      type="number"
      value={value}
      onChange={onChange}
      min={min}
      max={max}
      step={step}
      style={{ ...S.input, ...style }}
      onFocus={e => { e.target.style.borderColor = C.blue; }}
      onBlur={e => { e.target.style.borderColor = C.border; }}
    />
  );
}

// `help` is a <SettingHelp topic="..."/> shown after the title or label it
// explains (views/Settings/settingsHelp.js holds every explanation).
function PanelCard({ title, sub, help = null, color = C.blue, children }) {
  return (
    <div style={S.panel(color)}>
      <div style={S.panelHead(color)}>
        <span style={S.panelHeadDot(color)} />
        <span style={S.panelTitle}>{title}{help}</span>
        {sub && <span style={S.panelSub}>{sub}</span>}
      </div>
      <div style={S.panelBody}>{children}</div>
    </div>
  );
}

function FieldRow({ label, hint, help = null, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={S.fieldLabel}>{label}{help}</label>
      {children}
      {hint && <div style={S.fieldHint}>{hint}</div>}
    </div>
  );
}

// The urban band edges the server ships (models/DeliveryFeeConfig.js); the
// flat payload carries the rates only, so these are labels, not pricing.
const URBAN_BAND_LABELS = ["0 to 3 km", "3 to 8 km", "8 to 15 km", "Over 15 km"];

const formatUGX = (n) =>
  n !== undefined && n !== null && n !== "" ? `UGX ${Number(n).toLocaleString()}` : "—";
// Complexity is a MULTIPLIER on the fare (1.0 to 5), never a UGX add-on.
const formatMultiplier = (n) =>
  n !== undefined && n !== null && n !== "" && Number.isFinite(Number(n)) ? `x${Number(n).toFixed(2)}` : "—";

// The server sends `flat` (what the engine charges, in these field names) since
// 31 Oct 2026. The nested `config` is kept as the fallback for an older server;
// reading it here is what left Minimum fee, Base fee, Soft cap and Rounding
// blank and showed hardcoded tier and split numbers instead of the live ones.
const configToForm = (c, flat) => flat ? ({
  minFee: flat.minFee, baseFee: flat.baseFee,
  rateTier1: flat.rateTier1, rateTier2: flat.rateTier2, rateTier3: flat.rateTier3, rateTier4: flat.rateTier4,
  freeApproachKm: c?.freeApproachKm,
  approachRatePerKm: c?.approachRatePerKm,
  approachCustomerShare: c?.approachCustomerShare,
  maxApproachSurcharge: c?.maxApproachSurcharge,
  complexityStandard: flat.complexityStandard, complexityModerate: flat.complexityModerate,
  complexityDifficult: flat.complexityDifficult, complexityPremium: flat.complexityPremium,
  roundTo: flat.roundTo, softCap: flat.softCap,
  lusukuRate: flat.lusukuRate, partnerRate: flat.partnerRate, riderRate: flat.riderRate,
  roadFactor: flat.roadFactor,
}) : ({
  minFee: c.minFee, baseFee: c.baseFee,
  rateTier1: c.rateTiers?.[0]?.rate ?? 1000,
  rateTier2: c.rateTiers?.[1]?.rate ?? 700,
  rateTier3: c.rateTiers?.[2]?.rate ?? 550,
  rateTier4: c.rateTiers?.[3]?.rate ?? 450,
  freeApproachKm: c.freeApproachKm,
  approachRatePerKm: c.approachRatePerKm,
  approachCustomerShare: c.approachCustomerShare,
  maxApproachSurcharge: c.maxApproachSurcharge,
  complexityStandard: c.complexityTiers?.standard ?? 0,
  complexityModerate: c.complexityTiers?.moderate ?? 500,
  complexityDifficult: c.complexityTiers?.difficult ?? 1000,
  complexityPremium: c.complexityTiers?.premium ?? 1500,
  roundTo: c.roundTo, softCap: c.softCap,
  lusukuRate: c.commissionRates?.lusuku ?? 40,
  partnerRate: c.commissionRates?.partner ?? 18,
  riderRate: c.commissionRates?.rider ?? 42,
  roadFactor: c.distance?.roadFactor,
});

// A field with no value (the retired approach knobs, a blank input) compares
// as empty on both sides; `Number(undefined) === Number(undefined)` is NaN and
// never equal, which showed "Unsaved changes" on every fresh load.
const fieldValue = (v) => (v === "" || v === null || v === undefined ? "" : Number(v));
const formEqual = (a, b) =>
  Object.keys(a).every(k => fieldValue(a[k]) === fieldValue(b[k]));

export default function DeliveryFeeSettings({ canEdit = false }) {

  const [serverForm, setServerForm] = useState(null);
  const [form, setForm]           = useState(null);
  const [audit, setAudit]         = useState(null);   // { updatedAt, lastChange:{ by, role, ... } }
  const [loading, setLoading]     = useState(true);
  const [reloading, setReloading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [socketConn, setSocketConn] = useState(socket.connected);
  const [lastSync, setLastSync]   = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [vcVendorId, setVcVendorId]     = useState("");
  const [vcVendorType, setVcVendorType] = useState("");
  const [vcComplexity, setVcComplexity] = useState("standard");
  const [vcSaving, setVcSaving]   = useState(false);
  const [vcConfirm, setVcConfirm] = useState(false);
  const [vcResult, setVcResult]   = useState(null);
  const [simKm, setSimKm]         = useState(5);
  const [simComplexity, setSimComplexity] = useState("standard");
  const [simResult, setSimResult] = useState(null);

  // The server's defaults (DF_* config vars on Heroku over the shipped
  // fallbacks), in the same flat shape: what the engine uses for any field
  // left blank, and what each field's hint quotes.
  const [defaults, setDefaults] = useState(null);
  const defaultHint = (key, unit = "UGX") => {
    const v = defaults?.[key];
    if (v === undefined || v === null) return "";
    const shown = unit === "UGX" ? `UGX ${Number(v).toLocaleString()}` : unit === "x" ? `x${Number(v).toFixed(2)}` : `${v}${unit}`;
    return ` Default ${shown}.`;
  };

  const applyServerConfig = useCallback((c, flat, serverDefaults) => {
    const f = configToForm(c, flat);
    setServerForm(f);
    setForm(f);
    if (serverDefaults) setDefaults(serverDefaults);
    setLastSync(new Date());
  }, []);

  const fetchConfig = useCallback(async (quiet = false) => {
    try {
      if (quiet) setReloading(true); else setLoading(true);
      const res = await apiGetDeliveryFeeConfig();
      if (res.data?.success) applyServerConfig(res.data.config, res.data.flat, res.data.defaults);
    } catch { toast.error("Could not load delivery pricing"); }
    finally { setLoading(false); setReloading(false); }
  }, [applyServerConfig]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // Load the audit summary ("last changed by X on DATE"). Admin-only endpoint —
  // silently degrades for non-admins (they simply won't see the line).
  const fetchAudit = useCallback(async () => {
    if (!canEdit) return;
    try {
      const res = await apiGetDeliveryFeeConfigAudit();
      if (res.data?.success) setAudit(res.data.audit || null);
    } catch { /* non-fatal — line just won't render */ }
  }, [canEdit]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  useEffect(() => {
    const onConnect    = () => setSocketConn(true);
    const onDisconnect = () => setSocketConn(false);
    const onConfigUpdated = ({ currentConfig, flat }) => {
      applyServerConfig(currentConfig, flat);
      toast.info("Another session saved delivery pricing. The form now shows the saved values.", { autoClose: 4000 });
    };
    const onVcUpdated = ({ vendorName, complexity }) => {
      toast.info(`Another session set the pickup level of "${vendorName}" to ${complexity}.`, { autoClose: 4000 });
    };
    socket.on("connect",    onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("deliveryFee:configUpdated",          onConfigUpdated);
    socket.on("deliveryFee:vendorComplexityUpdated", onVcUpdated);
    return () => {
      socket.off("connect",    onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("deliveryFee:configUpdated",          onConfigUpdated);
      socket.off("deliveryFee:vendorComplexityUpdated", onVcUpdated);
    };
  }, [applyServerConfig]);

  const hc = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const doSave = async () => {
    if (!canEdit) { toast.error("Changing delivery pricing needs a super administrator."); return; }
    const splits = Number(form.lusukuRate) + Number(form.partnerRate) + Number(form.riderRate);
    if (Math.abs(splits - 100) > 0.01) {
      toast.error(`The delivery partner split must add up to 100%. It is ${splits.toFixed(1)}% now.`); return;
    }
    try {
      setSaving(true);
      // A blank field is "not provided": it is left out so the engine keeps
      // using the server default for it, rather than being saved as 0.
      const payload = Object.fromEntries(
        Object.entries(form)
          .filter(([, v]) => v !== "" && v !== null && v !== undefined)
          .map(([k, v]) => [k, Number(v)]),
      );
      const res = await apiUpdateDeliveryFeeConfig(payload);
      if (res.data?.success) { applyServerConfig(res.data.currentConfig, res.data.flat); fetchAudit(); toast.success("Delivery pricing saved. New quotes use it within a minute."); }
      else toast.error(res.data?.message || "Update failed");
    } catch (err) { toast.error(err.response?.data?.message || "Could not save delivery pricing"); }
    finally { setSaving(false); }
  };

  const doSetComplexity = async () => {
    if (!canEdit) { toast.error("Setting a pickup level needs a super administrator."); return; }
    if (!vcVendorId.trim()) { toast.error("Vendor ID is required"); return; }
    try {
      setVcSaving(true); setVcResult(null);
      const res = await apiSetVendorComplexity(vcVendorId.trim(), vcVendorType || undefined, vcComplexity);
      if (res.data?.success) { setVcResult(res.data.vendor); fetchAudit(); toast.success(res.data.message); }
      else toast.error(res.data?.message || "Failed");
    } catch (err) { toast.error(err.response?.data?.message || "Could not set the pickup level"); }
    finally { setVcSaving(false); }
  };

  const handleSimulate = () => {
    const base = Number(form.baseFee), km = Number(simKm);
    const tiers = [
      { maxKm: 3, rate: Number(form.rateTier1) },
      { maxKm: 8, rate: Number(form.rateTier2) },
      { maxKm: 15, rate: Number(form.rateTier3) },
      { maxKm: Infinity, rate: Number(form.rateTier4) },
    ];
    const tier   = tiers.find(t => km <= t.maxKm) || tiers[3];
    const dist   = km * tier.rate;
    const cMap   = { standard: Number(form.complexityStandard), moderate: Number(form.complexityModerate), difficult: Number(form.complexityDifficult), premium: Number(form.complexityPremium) };
    const multiplier = Number.isFinite(cMap[simComplexity]) && cMap[simComplexity] >= 1 ? cMap[simComplexity] : 1;
    const complex = Math.round((base + dist) * (multiplier - 1));
    const raw    = (base + dist) * multiplier;
    const rnd    = Number(form.roundTo) || 500;
    const fee    = Math.max(Number(form.minFee), Math.min(Number(form.softCap), Math.round(raw / rnd) * rnd));
    setSimResult({ fee, base, dist: Math.round(dist), complex, raw: Math.round(raw), tierRate: tier.rate, km });
  };

  if (loading || !form) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
      <Spinner animation="border" style={{ color: C.accent, width: 44, height: 44 }} />
      <p style={{ color: C.textLight, marginTop: 14, fontSize: 13 }}>Loading delivery pricing…</p>
    </div>
  );

  // "Last changed by X on DATE" line, sourced from the backend audit history.
  const lc = audit?.lastChange;
  const lastChangedText = lc
    ? `Last changed by ${lc.byEmail || (audit.updatedByEmail) || (lc.by ? `user ${String(lc.by).slice(-6)}` : "unknown")}`
      + (lc.role ? ` (${lc.role})` : "")
      + (lc.at ? ` on ${new Date(lc.at).toLocaleString()}` : "")
      + (lc.version ? ` · v${lc.version}` : "")
    : (audit?.updatedAt
        ? `Last changed on ${new Date(audit.updatedAt).toLocaleString()}${audit.version ? ` · v${audit.version}` : ""}`
        : null);

  const LastChangedLine = ({ light = false }) =>
    lastChangedText ? (
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11,
        color: light ? "rgba(255,255,255,0.55)" : C.textLight,
      }}>
        <FiClock size={10} /> {lastChangedText}
      </div>
    ) : null;

  // ── READ-ONLY VIEW for non-super-admins (vendors / staff) ───────────────────
  // The breakdown stays visible; every editing control is removed. The backend
  // PATCH gate (canEditDeliveryConfig → 403) is the real enforcement.
  if (!canEdit) {
    return (
      <div style={S.page}>
        <div style={S.heroBand}>
          <div>
            <div style={{ ...S.heroTitle, display: "flex", alignItems: "center", gap: 10 }}>
              <FiShield size={20} /> Delivery fee engine
              <SettingHelp topic="deliveryFeeEngine" placement="bottom" />
            </div>
            <div style={S.heroSub}>
              Read only. Delivery pricing is changed by a Lusuku super administrator.
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={S.heroWarning}>
                <FiShield size={11} /> You can view delivery pricing but not change it.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
            {[
              { label: "Cheapest urban", val: formatUGX(form.minFee) },
              { label: "Urban start",    val: formatUGX(form.baseFee) },
              { label: "Urban cap",      val: formatUGX(form.softCap) },
            ].map(({ label, val }) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.08)", borderRadius: C.radius, padding: "10px 18px", textAlign: "center", minWidth: 100 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{val}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.50)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        <Row>
          <Col md={6}>
            <PanelCard title="Urban fare and limits" help={<SettingHelp topic="deliveryFareFormula" />} color={C.teal}
              sub={<span style={{ display: "flex", alignItems: "center", gap: 4, color: C.textLight, fontSize: 11 }}><FiActivity size={10} /> Live snapshot</span>}>
              {[
                { label: "Cheapest urban delivery", val: formatUGX(form.minFee) },
                { label: "Urban starting fare", val: formatUGX(form.baseFee) },
                { label: "Urban fare cap", val: formatUGX(form.softCap) },
                { label: "Round fares to", val: formatUGX(form.roundTo) },
              ].map(({ label, val }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "7px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                  <span style={{ color: C.textMid }}>{label}</span>
                  <span style={{ fontWeight: 700, color: C.text }}>{val}</span>
                </div>
              ))}
              <div style={{ marginTop: 14 }}>
                <div style={S.sectionBreak}><FiMapPin size={9} /> Urban distance rates, UGX per km<SettingHelp topic="deliveryDistanceRates" /></div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[form.rateTier1, form.rateTier2, form.rateTier3, form.rateTier4].map((rate, i) => (
                    <div key={i} style={{ background: C.tealSoft, border: `1px solid ${C.teal}30`,
                      borderRadius: C.radiusSm, padding: "4px 10px", fontSize: 11, color: C.teal, fontWeight: 700 }}>
                      {URBAN_BAND_LABELS[i]} · {Number(rate).toLocaleString()}
                    </div>
                  ))}
                </div>
              </div>
            </PanelCard>
          </Col>
          <Col md={6}>
            <PanelCard title="Pickup difficulty multipliers" help={<SettingHelp topic="deliveryPickupDifficulty" />} color={C.purple}
              sub={<span style={{ display: "flex", alignItems: "center", gap: 4, color: C.textLight, fontSize: 11 }}><FiSliders size={10} /> Set per vendor</span>}>
              {Object.entries(COMPLEXITY_META).map(([k, { label, color, soft }]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ ...S.complexityBadge(color, soft), display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {COMPLEXITY_ICONS[k]} {label}
                  </span>
                  <span style={{ fontWeight: 700, color: C.text, fontSize: 13 }}>{formatMultiplier(form[`complexity${label}`])}</span>
                </div>
              ))}
              <div style={{ background: C.blueSoft, borderRadius: C.radiusSm, padding: "8px 12px",
                fontSize: 11, color: C.blue, marginTop: 14, display: "flex", alignItems: "center", gap: 6 }}>
                <FiShield size={11} /> Ask a Lusuku super administrator to change pricing.
              </div>
            </PanelCard>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <PanelCard title="Road distance" help={<SettingHelp topic="deliveryRoadDistance" />} color={C.blue}>
              <RoadDistanceHealth />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "7px 0", marginTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ color: C.textMid }}>Road factor for the straight-line estimate<SettingHelp topic="deliveryRoadFactor" /></span>
                <span style={{ fontWeight: 700, color: C.text }}>{formatMultiplier(form.roadFactor)}</span>
              </div>
            </PanelCard>
          </Col>
        </Row>
      </div>
    );
  }

  const splitSum = Number(form.lusukuRate) + Number(form.partnerRate) + Number(form.riderRate);
  const splitOk  = Math.abs(splitSum - 100) < 0.01;
  const isDirty  = serverForm ? !formEqual(form, serverForm) : false;
  const canSave  = isDirty && splitOk && !saving;
  const maxTier  = Math.max(Number(form.rateTier1), Number(form.rateTier2), Number(form.rateTier3), Number(form.rateTier4));

  return (
    <div style={S.page}>

      {/* ── Confirm: Save Config ── */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered size="sm">
        <Modal.Header style={{ background: C.header, color: "#fff", borderBottom: "none", padding: "16px 20px" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FiAlertTriangle size={15} color={C.orange} /> Save delivery pricing?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13, color: C.textMid, padding: "18px 20px" }}>
          These values price <strong>every new delivery quote</strong>.
          <div style={{ marginTop: 12, padding: "8px 12px", background: C.accentSoft, borderRadius: C.radiusSm, color: C.accent, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <FiAlertTriangle size={11} /> Saved in the database. New quotes use them at once on this server and within a minute on the others. Orders already placed are not re-priced.
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}`, padding: "12px 20px", gap: 8 }}>
          <Button size="sm" variant="light" onClick={() => setShowConfirm(false)} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
            <FiX size={11} /> Cancel
          </Button>
          <Button size="sm" onClick={() => { setShowConfirm(false); doSave(); }}
            style={{ background: ACTION_NAVY, border: "none", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}>
            <FiCheck size={11} /> Save pricing
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Confirm: Vendor Complexity ── */}
      <Modal show={vcConfirm} onHide={() => setVcConfirm(false)} centered size="sm">
        <Modal.Header style={{ background: C.header, color: "#fff", borderBottom: "none", padding: "16px 20px" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FiMapPin size={15} color={C.teal} /> Set this vendor's pickup level?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13, color: C.textMid, padding: "18px 20px" }}>
          Set the pickup level to{" "}
          <span style={S.complexityBadge(COMPLEXITY_META[vcComplexity]?.color, COMPLEXITY_META[vcComplexity]?.soft)}>
            {COMPLEXITY_ICONS[vcComplexity]} {COMPLEXITY_META[vcComplexity]?.label}
          </span>{" "}
          for vendor <code style={{ fontSize: 11 }}>{vcVendorId.slice(0, 10)}…</code>?<br /><br />
          Every new delivery quote from this vendor and its branches will use it.
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}`, padding: "12px 20px", gap: 8 }}>
          <Button size="sm" variant="light" onClick={() => setVcConfirm(false)} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
            <FiX size={11} /> Cancel
          </Button>
          <Button size="sm" onClick={() => { setVcConfirm(false); doSetComplexity(); }}
            style={{ background: C.teal, border: "none", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}>
            <FiCheck size={11} /> Save level
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Hero Band ── */}
      <div style={S.heroBand}>
        <div>
          <div style={{ ...S.heroTitle, display: "flex", alignItems: "center", gap: 10 }}>
            <FiSettings size={20} /> Delivery fee engine
            <SettingHelp topic="deliveryFeeEngine" placement="bottom" />
          </div>
          <div style={S.heroSub}>
            Prices every delivery from the map. A save reaches new quotes <strong style={{ color: "#fff" }}>within a minute</strong>.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            <div style={S.heroSaved}>
              <FiCheck size={11} /> Saved in the database, so changes survive a restart
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11,
              color: socketConn ? "#4dffc3" : "#ff6b88",
              background: socketConn ? "rgba(0,182,155,0.15)" : "rgba(230,55,87,0.15)",
              border: `1px solid ${socketConn ? "rgba(0,182,155,0.3)" : "rgba(230,55,87,0.3)"}`,
              borderRadius: C.radiusSm, padding: "4px 10px" }}>
              {socketConn ? <FiWifi size={11} /> : <FiWifiOff size={11} />}
              {socketConn ? "Live" : "Offline"}
            </div>
            {lastSync && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "rgba(255,255,255,0.50)" }}>
                <FiClock size={10} /> Synced {lastSync.toLocaleTimeString()}
              </div>
            )}
            <LastChangedLine light />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
          {[
            { label: "Cheapest urban", val: formatUGX(form.minFee) },
            { label: "Urban cap",      val: formatUGX(form.softCap) },
            { label: "Partner split",  val: `${splitSum.toFixed(0)}%`, warn: !splitOk },
          ].map(({ label, val, warn }) => (
            <div key={label} style={{ background: "rgba(255,255,255,0.08)", borderRadius: C.radius, padding: "10px 18px", textAlign: "center", minWidth: 100 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: warn ? "#ff6b88" : "#fff" }}>{val}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.50)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Dirty Banner ── */}
      {isDirty && (
        <div style={{ background: "#fffbea", border: `1px solid ${C.orange}50`, borderRadius: C.radiusSm,
          padding: "10px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
          <FiAlertTriangle size={14} color={C.orange} />
          <span style={{ color: C.orange, fontWeight: 600 }}>Unsaved changes.</span>
          <span style={{ color: C.textMid }}>Save to apply them to new quotes, or discard to go back to the saved values.</span>
          <button type="button" onClick={() => { setForm(serverForm); setSimResult(null); }}
            style={{ marginLeft: "auto", background: ACTION_NAVY, border: "none",
              borderRadius: C.radiusSm, fontSize: 11, color: "#fff", padding: "4px 10px",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
            <FiX size={10} /> Discard
          </button>
        </div>
      )}

      {/* ── KPI Row ── */}
      <Row style={{ marginBottom: 4 }}>
        {[
          { label: "Urban starting fare",     val: formatUGX(form.baseFee),           color: C.blue,   soft: C.blueSoft,   Icon: FiDollarSign },
          { label: "Cheapest urban delivery", val: formatUGX(form.minFee),            color: C.teal,   soft: C.tealSoft,   Icon: FiSliders    },
          { label: "Urban fare cap",          val: formatUGX(form.softCap),           color: C.orange, soft: C.orangeSoft, Icon: FiBarChart2  },
          { label: "Premium pickup",          val: formatMultiplier(form.complexityPremium), color: C.accent, soft: C.accentSoft, Icon: FiZap        },
        ].map(({ label, val, color, soft, Icon }) => (
          <Col key={label} md={3} sm={6}>
            <div style={S.kpiCard(color, soft)}>
              <div style={S.kpiIcon(color, soft)}><Icon size={18} /></div>
              <div>
                <div style={S.kpiVal}>{val}</div>
                <div style={S.kpiLabel}>{label}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <form onSubmit={e => { e.preventDefault(); if (canSave) setShowConfirm(true); }}>
        <Row>
          <Col md={12}>
            <PanelCard title="Road distance" help={<SettingHelp topic="deliveryRoadDistance" />} color={C.blue}>
              <Row>
                <Col md={8}><RoadDistanceHealth /></Col>
                <Col md={4}>
                  <FieldRow label="Road factor for the straight-line estimate" help={<SettingHelp topic="deliveryRoadFactor" />}
                    hint={`Used only when Google gives no road distance: the straight line times this, from 1 to 3.${defaultHint("roadFactor", "x")}`}>
                    <NumInput value={form.roadFactor ?? ""} onChange={hc("roadFactor")} min={1} max={3} step={0.05} />
                  </FieldRow>
                </Col>
              </Row>
            </PanelCard>
          </Col>
        </Row>
        <Row>
          {/* ── Col 1: Base Fees + Approach Surcharge ── */}
          <Col md={4}>
            <PanelCard title="Urban fare and limits" help={<SettingHelp topic="deliveryFareFormula" />} color={C.blue}
              sub={<span style={{ display: "flex", alignItems: "center", gap: 4, color: C.textLight, fontSize: 11 }}><FiDollarSign size={10} /> UGX, urban zone</span>}>
              {[
                { key: "minFee",  label: "Cheapest urban delivery", hint: "No urban delivery is quoted below this; the cost floor can set a higher price.", help: <SettingHelp topic="deliveryMinimumFee" /> },
                { key: "baseFee", label: "Urban starting fare", hint: "Every urban fare starts here before distance is added.", help: <SettingHelp topic="deliveryBaseFare" /> },
                { key: "softCap", label: "Urban fare cap", hint: "The tariff stops here, raised for bigger vehicles; the cost floor can still go above it.", help: <SettingHelp topic="deliverySoftCap" /> },
                { key: "roundTo", label: "Round fares to", hint: "The tariff is rounded to the nearest multiple of this, in every zone.", help: <SettingHelp topic="deliveryRoundingStep" /> },
              ].map(({ key, label, hint, help }) => (
                <FieldRow key={key} label={label} help={help} hint={`${hint}${defaultHint(key)}`}>
                  <NumInput value={form[key]} onChange={hc(key)} />
                </FieldRow>
              ))}
            </PanelCard>

            {/* The approach surcharge knobs (free radius, rate, customer share,
                cap) are RETIRED: the server records them in the audit and the
                engine reads none of them (RETIRED_FLAT_FIELDS). The panel that
                edited them showed four blank fields and "NaN%" once the screen
                started reading live values, so it is gone. */}
          </Col>

          {/* ── Col 2: Rate Tiers + Complexity ── */}
          <Col md={4}>
            <PanelCard title="Urban distance rates" help={<SettingHelp topic="deliveryDistanceRates" />} color={C.teal}
              sub={<span style={{ display: "flex", alignItems: "center", gap: 4, color: C.textLight, fontSize: 11 }}><FiMapPin size={10} /> Charged band by band</span>}>
              <p style={{ fontSize: 11, color: C.textLight, marginBottom: 14 }}>
                Each kilometre is charged at the rate of the band it falls in, like tax brackets, so a longer trip never costs less. Only distance up to the urban limit is charged at these rates.
              </p>
              {[
                { key: "rateTier1", label: "First 3 km",   band: "short" },
                { key: "rateTier2", label: "3 to 8 km",    band: "medium" },
                { key: "rateTier3", label: "8 to 15 km",   band: "long" },
                { key: "rateTier4", label: "Beyond 15 km", band: "far", help: <SettingHelp topic="deliveryRateTier4" /> },
              ].map(({ key, label, help }) => {
                const pct = maxTier > 0 ? (Number(form[key]) / maxTier) * 100 : 0;
                return (
                  <FieldRow key={key} label={`${label} (UGX per km)`} help={help} hint={defaultHint(key).trim() || undefined}>
                    <NumInput value={form[key]} onChange={hc(key)} />
                    <div style={S.tierBar(pct, C.teal)} />
                    <div style={{ fontSize: 10, color: C.teal, textAlign: "right", marginTop: 1 }}>UGX {Number(form[key]).toLocaleString()} per km</div>
                  </FieldRow>
                );
              })}
            </PanelCard>

            <PanelCard title="Pickup difficulty multipliers" help={<SettingHelp topic="deliveryPickupDifficulty" />} color={C.purple}
              sub={<span style={{ display: "flex", alignItems: "center", gap: 4, color: C.textLight, fontSize: 11 }}><FiSliders size={10} /> Set per vendor</span>}>
              <p style={{ fontSize: 11, color: C.textLight, marginBottom: 14 }}>
                Each vendor carries one level, set in the panel below or on Manage Brands. Every quote from that vendor multiplies the fare by its level's number.
              </p>
              {Object.entries(COMPLEXITY_META).map(([tier, { label, color, soft }]) => {
                const key = `complexity${label}`;
                return (
                  <FieldRow key={tier} label={
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ ...S.complexityBadge(color, soft), display: "inline-flex", alignItems: "center", gap: 4 }}>{COMPLEXITY_ICONS[tier]} {label}</span>
                      <span style={{ color: C.textLight, fontWeight: 400, fontSize: 10, textTransform: "none", letterSpacing: 0 }}>multiplier, 1.00 to 5.00</span>
                    </span>
                  } hint={`${defaultHint(key, "x").trim()} 1.40 means the fare is 40% higher for this pickup.`}>
                    <NumInput value={form[key]} onChange={hc(key)} step={0.05} />
                  </FieldRow>
                );
              })}
            </PanelCard>
          </Col>

          {/* ── Col 3: Commission Splits + Simulator ── */}
          <Col md={4}>
            <PanelCard title="Delivery partner split" help={<SettingHelp topic="deliveryPartnerSplit" />} color={splitOk ? C.teal : C.accent}
              sub={
                <span style={{ display: "flex", alignItems: "center", gap: 4, color: splitOk ? C.teal : C.accent, fontWeight: 700, fontSize: 12 }}>
                  {splitOk ? <FiCheck size={11} /> : <FiAlertTriangle size={11} />}
                  {splitOk ? "100%" : `${splitSum.toFixed(1)}%, not 100`}
                </span>
              }
            >
              {[
                { key: "lusukuRate",  label: "Lusuku's share of the fee",  color: C.blue,   help: <SettingHelp topic="deliverySplitLusuku" /> },
                { key: "partnerRate", label: "Partner's share of the fee", color: C.purple, help: <SettingHelp topic="deliverySplitPartner" /> },
                { key: "riderRate",   label: "Rider's share of the fee",   color: C.teal,   help: <SettingHelp topic="deliverySplitRider" /> },
              ].map(({ key, label, color, help }) => (
                <FieldRow key={key} label={`${label} (%)`} help={help} hint={defaultHint(key, "%").trim() || undefined}>
                  <NumInput value={form[key]} onChange={hc(key)} step={0.5} max={100} />
                  <div style={S.tierBar(Number(form[key]), color)} />
                  <div style={{ fontSize: 10, color, textAlign: "right", marginTop: 1, fontWeight: 700 }}>{Number(form[key]).toFixed(1)}%</div>
                </FieldRow>
              ))}
              {/* Stacked split bar */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 10, color: C.textLight, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.5px" }}>Visual split</div>
                <div style={S.splitBar}>
                  <div style={{ flex: Number(form.lusukuRate),  background: C.blue,   transition: "flex 0.3s" }} title={`Lusuku ${form.lusukuRate}%`} />
                  <div style={{ flex: Number(form.partnerRate), background: C.purple, transition: "flex 0.3s" }} title={`Partner ${form.partnerRate}%`} />
                  <div style={{ flex: Number(form.riderRate),   background: C.teal,   transition: "flex 0.3s" }} title={`Rider ${form.riderRate}%`} />
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 10 }}>
                  <span style={{ color: C.blue   }}>■ Lusuku {form.lusukuRate}%</span>
                  <span style={{ color: C.purple }}>■ Partner {form.partnerRate}%</span>
                  <span style={{ color: C.teal   }}>■ Rider {form.riderRate}%</span>
                </div>
              </div>
            </PanelCard>

            {/* ── Fee Simulator ── */}
            <PanelCard title="Rough fee estimate" help={<SettingHelp topic="deliveryFeeEstimate" />} color={C.blue}
              sub={<span style={{ display: "flex", alignItems: "center", gap: 4, color: C.textLight, fontSize: 11 }}><FiActivity size={10} /> From the form, not the engine</span>}>
              <p style={{ fontSize: 11, color: C.textLight, marginBottom: 12 }}>
                A quick urban sum from the form values. Fee Transparency shows what the engine really charges.
              </p>
              <Row>
                <Col xs={6}>
                  <FieldRow label="Distance (km)">
                    <NumInput value={simKm} onChange={e => setSimKm(e.target.value)} step={0.5} />
                  </FieldRow>
                </Col>
                <Col xs={6}>
                  <FieldRow label="Pickup level">
                    <AppSelect
                      value={simComplexity}
                      onChange={e => setSimComplexity(e.target.value)}
                      style={{ ...S.input }}
                    >
                      {Object.entries(COMPLEXITY_META).map(([val, { label }]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </AppSelect>
                  </FieldRow>
                </Col>
              </Row>
              <button type="button" onClick={handleSimulate} style={S.btnBlue}>
                <FiZap size={12} /> Estimate
              </button>

              {simResult && (
                <div style={{ marginTop: 14 }}>
                  <hr style={S.divider} />
                  {[
                    { label: `${simResult.km} km at UGX ${Number(simResult.tierRate).toLocaleString()} per km`, val: formatUGX(simResult.dist) },
                    { label: "Starting fare",      val: formatUGX(simResult.base) },
                    { label: "Pickup difficulty",  val: formatUGX(simResult.complex) },
                    { label: "Before limits",      val: formatUGX(simResult.raw) },
                  ].map(({ label, val }) => (
                    <div key={label} style={S.simRow(false)}>
                      <span style={S.simLabel(false)}>{label}</span>
                      <span style={S.simValue(false)}>{val}</span>
                    </div>
                  ))}
                  <div style={{ ...S.simRow(true), marginTop: 6, border: `1px solid ${C.teal}30` }}>
                    <span style={{ ...S.simLabel(true), fontSize: 13, display: "flex", alignItems: "center", gap: 5 }}>
                      <FiZap size={13} /> Estimated fee
                    </span>
                    <span style={{ ...S.simValue(true), fontSize: 20 }}>{formatUGX(simResult.fee)}</span>
                  </div>
                </div>
              )}
            </PanelCard>
          </Col>
        </Row>

        {/* ── Save Bar ── */}
        <div style={{ background: C.panel, border: `1px solid ${isDirty ? C.orange + "60" : C.border}`,
          borderRadius: C.radius, padding: "16px 24px", display: "flex", alignItems: "center",
          gap: 12, marginBottom: 28, boxShadow: C.shadow, transition: "border-color 0.2s", flexWrap: "wrap" }}>
          <button type="submit" disabled={!canSave} style={S.btnPrimary(!canSave)}
            title={!isDirty ? "No changes to save" : !splitOk ? "Make the delivery partner split add up to 100% first" : "Save delivery pricing"}>
            {saving
              ? <><Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> Saving…</>
              : <><FiSave size={14} /> Save delivery pricing</>}
          </button>
          <button type="button" style={{ ...S.btnSecondary, display: "flex", alignItems: "center", gap: 6 }}
            onClick={() => fetchConfig(true)} disabled={reloading}>
            {reloading ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} /> : <FiRefreshCw size={13} />}
            {reloading ? "Reloading…" : "Reload saved values"}
          </button>
          {!isDirty && !saving && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, color: C.teal, fontSize: 12 }}>
              <FiCheck size={12} /> Matches the saved values
            </span>
          )}
          {isDirty && !splitOk && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, color: C.accent, fontSize: 12, fontWeight: 600 }}>
              <FiAlertTriangle size={12} /> The delivery partner split adds up to {splitSum.toFixed(1)}%; it must be 100%
            </span>
          )}
          {isDirty && splitOk && !saving && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, color: C.orange, fontSize: 12, fontWeight: 600 }}>
              <FiAlertTriangle size={12} /> Unsaved changes. Save to apply them.
            </span>
          )}
        </div>
      </form>

      {/* ── Bottom Row: Vendor Complexity + Live Config + Reference ── */}
      <Row>
        {/* Vendor Complexity Setter */}
        <Col md={4}>
          <PanelCard title="Set a vendor's pickup level" help={<SettingHelp topic="deliveryVendorPickupLevel" />} color={C.accent}
            sub={<span style={{ display: "flex", alignItems: "center", gap: 4, color: C.textLight, fontSize: 11 }}><FiMapPin size={10} /> Saved on the vendor</span>}>
            <p style={{ fontSize: 11, color: C.textLight, marginBottom: 14 }}>
              Every new quote from this vendor and its branches uses the level saved here.
            </p>
            <form onSubmit={e => { e.preventDefault(); if (vcVendorId.trim()) setVcConfirm(true); else toast.error("Vendor ID required"); }}>
              <FieldRow label="Vendor ID" hint="The vendor's 24 character ID, for example 669fdbcc…">
                <input
                  type="text"
                  value={vcVendorId}
                  onChange={e => setVcVendorId(e.target.value)}
                  placeholder="669fdbccb0f4117fc8938aef"
                  pattern="[a-fA-F0-9]{24}"
                  style={S.input}
                />
              </FieldRow>
              <FieldRow label="Vendor type" hint="Leave on Auto-detect to look in every vendor type.">
                <AppSelect value={vcVendorType} onChange={e => setVcVendorType(e.target.value)} style={S.input}>
                  <option value="">Auto-detect</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="retailer">Retailer / Wholesaler</option>
                  <option value="brand">Brand</option>
                </AppSelect>
              </FieldRow>
              <FieldRow label="Pickup level">
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                  {Object.entries(COMPLEXITY_META).map(([val, { label, color, soft }]) => (
                    <button key={val} type="button" onClick={() => setVcComplexity(val)}
                      style={{ ...S.complexityBadge(color, soft),
                        border: vcComplexity === val ? `2px solid ${color}` : `1px solid ${color}30`,
                        cursor: "pointer", padding: "5px 12px", fontSize: 12,
                        fontWeight: vcComplexity === val ? 800 : 600,
                        transform: vcComplexity === val ? "scale(1.05)" : "scale(1)",
                        transition: "all 0.12s",
                        display: "inline-flex", alignItems: "center", gap: 4,
                      }}>
                      {COMPLEXITY_ICONS[val]} {label}
                    </button>
                  ))}
                </div>
              </FieldRow>
              <button type="submit" disabled={vcSaving || !vcVendorId.trim()}
                style={{ ...S.btnTeal, marginTop: 8, opacity: !vcVendorId.trim() ? 0.45 : 1,
                  cursor: !vcVendorId.trim() ? "not-allowed" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6 }}>
                {vcSaving ? <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} /> : <FiCheck size={12} />}
                {vcSaving ? "Saving…" : "Save pickup level"}
              </button>
            </form>

            {vcResult && (
              <div style={{ ...S.successBanner, display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <FiCheck size={13} />
                  <strong>{vcResult.name}</strong> →
                  <span style={{ ...S.complexityBadge(COMPLEXITY_META[vcResult.pickupComplexity]?.color, COMPLEXITY_META[vcResult.pickupComplexity]?.soft), display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {COMPLEXITY_ICONS[vcResult.pickupComplexity]} {vcResult.pickupComplexity}
                  </span>
                  <span style={{ color: C.textLight }}>({vcResult.vendorType})</span>
                </div>
                <div style={{ fontSize: 10, color: C.textLight }}>ID: {vcResult._id}</div>
              </div>
            )}
          </PanelCard>
        </Col>

        {/* Live Config from Server (always shown, reads serverForm) */}
        <Col md={4}>
          <PanelCard title="Saved values on the server" color={C.teal}
            sub={
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.textLight }}>
                {reloading ? <Spinner animation="border" size="sm" style={{ width: 10, height: 10 }} /> : <FiActivity size={10} />}
                {reloading ? "Refreshing…" : "As last loaded"}
              </span>
            }>
            {serverForm && [
              { id: "minFee",   label: "Cheapest urban delivery", val: formatUGX(serverForm.minFee) },
              { id: "baseFee",  label: "Urban starting fare",     val: formatUGX(serverForm.baseFee) },
              { id: "softCap",  label: "Urban fare cap",          val: formatUGX(serverForm.softCap) },
              { id: "roundTo",  label: "Round fares to",          val: formatUGX(serverForm.roundTo) },
              { id: "roadFactor", label: "Road factor (straight line)", val: formatMultiplier(serverForm.roadFactor) },
              // The four approach values are retired: the engine reads none of them.
              { id: "approach", label: <>Approach surcharge<SettingHelp topic="deliveryApproachSurchargeRetired" /></>, val: "Retired, not charged" },
            ].map(({ id, label, val }) => (
              <div key={id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "7px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ color: C.textMid }}>{label}</span>
                <span style={{ fontWeight: 700, color: C.text }}>{val}</span>
              </div>
            ))}
            <div style={{ marginTop: 14 }}>
              <div style={S.sectionBreak}><FiMapPin size={9} /> Urban distance rates, UGX per km</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {serverForm && [serverForm.rateTier1, serverForm.rateTier2, serverForm.rateTier3, serverForm.rateTier4].map((rate, i) => (
                  <div key={i} style={{ background: C.tealSoft, border: `1px solid ${C.teal}30`,
                    borderRadius: C.radiusSm, padding: "4px 10px", fontSize: 11, color: C.teal, fontWeight: 700 }}>
                    {URBAN_BAND_LABELS[i]} · {Number(rate).toLocaleString()}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <div style={S.sectionBreak}><FiSliders size={9} /> Pickup multipliers</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {Object.entries(COMPLEXITY_META).map(([k, { label, color, soft }]) => (
                  <div key={k} style={{ ...S.complexityBadge(color, soft), fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {COMPLEXITY_ICONS[k]} {k} {formatMultiplier(serverForm?.[`complexity${label}`])}
                  </div>
                ))}
              </div>
            </div>
          </PanelCard>
        </Col>

        {/* Complexity Reference */}
        <Col md={4}>
          <PanelCard title="When to use each pickup level" color={C.purple}
            sub={<span style={{ display: "flex", alignItems: "center", gap: 4, color: C.textLight, fontSize: 11 }}><FiChevronRight size={10} /> Guidance for staff</span>}>
            {[
              { tier: "standard",  color: C.textMid,  soft: "#f0f2f5",    desc: "Easy access and parking. The fare is not raised." },
              { tier: "moderate",  color: C.blue,     soft: C.blueSoft,   desc: "Tight parking or brief wait is common." },
              { tier: "difficult", color: C.orange,   soft: C.orangeSoft, desc: "Narrow lanes, complex building entry." },
              { tier: "premium",   color: C.accent,   soft: C.accentSoft, desc: "Highly congested zone, long rider wait." },
            ].map(({ tier, color, soft, desc }) => {
              const { label } = COMPLEXITY_META[tier];
              return (
                <div key={tier} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: soft, display: "flex",
                    alignItems: "center", justifyContent: "center", color, flexShrink: 0 }}>
                    {COMPLEXITY_ICONS[tier]}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                      <span style={{ ...S.complexityBadge(color, soft), display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {COMPLEXITY_ICONS[tier]} {tier}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color }}>
                        {formatMultiplier(form[`complexity${label}`])}
                      </span>
                    </div>
                    <p style={{ fontSize: 11, color: C.textLight, margin: 0 }}>{desc}</p>
                  </div>
                </div>
              );
            })}
            <div style={{ background: C.blueSoft, borderRadius: C.radiusSm, padding: "8px 12px",
              fontSize: 11, color: C.blue, marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
              <FiActivity size={11} /> Shows the form values. Save to apply them.
            </div>
          </PanelCard>
        </Col>
      </Row>
    </div>
  );
}
